import Download from "@mui/icons-material/Download";
import FileDownload from "@mui/icons-material/FileDownload";
import { Button, IconButton, Link, Tooltip, Typography } from "@mui/material";
import { GridColumnVisibilityModel } from "@mui/x-data-grid-premium";
import { useExportData, ExportParams } from "hooks/useExportData";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useCallback, useMemo, useState } from "react";

import { useAlerts } from "./Alerts/AlertProvider";
import { Dialog } from "./Dialog";

// Type for grid settings stored in localStorage
interface GridSettings {
  pageSize: number;
  sortModel: unknown[];
  columnVisibilityModel: GridColumnVisibilityModel;
  columnWidthModel: Record<string, number>;
  mobile?: {
    columnVisibilityModel: GridColumnVisibilityModel;
    columnWidthModel: Record<string, number>;
  };
  desktop?: {
    columnVisibilityModel: GridColumnVisibilityModel;
    columnWidthModel: Record<string, number>;
  };
}

export type ExportDataButtonProps = ExportParams & {
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  timeoutInSeconds?: number;
  dialog?: boolean;
  dialogTitle?: string;
  generateMessage?: string;
  iconOnly?: boolean;
  title?: string;
  /**
   * The localStorage key for grid settings (e.g., "grid-settings-rcra-info-hd-handlers").
   * When provided, the column visibility model will be included in the export,
   * allowing the backend to filter out hidden columns.
   */
  gridSettingsKey?: string;
};

export const ExportDataButton: React.FC<ExportDataButtonProps> = ({
  tenantId,
  facilityId,
  reportingYear,
  exportType,
  fileName,
  timeoutInSeconds,
  dialog = false,
  dialogTitle = "Generating Export",
  generateMessage = "Your file is being generated. This may take a few minutes...",
  iconOnly = false,
  title = "Export",
  filter,
  gridSettingsKey,
  ...props
}) => {
  const alerts = useAlerts();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { startExport, downloadFile, status } = useExportData({
    timeoutInSeconds,
    autoDownload: !dialog, // Auto-download for non-dialog mode
  });

  // Access grid settings from localStorage when gridSettingsKey is provided
  const [gridSettings] = useLocalStorageState<GridSettings | null>(
    gridSettingsKey ? `grid-settings-${gridSettingsKey}` : "",
    {
      defaultValue: null,
    }
  );

  // Get the column visibility model for desktop view
  const columnVisibility = useMemo(() => {
    if (!gridSettingsKey || !gridSettings) {
      return undefined;
    }
    return gridSettings?.desktop?.columnVisibilityModel || undefined;
  }, [gridSettingsKey, gridSettings]);

  // Create export params from current props
  const getExportParams = useCallback(
    (): ExportParams => ({
      tenantId,
      fileName,
      facilityId,
      reportingYear,
      exportType,
      filter,
      columnVisibility,
    }),
    [tenantId, fileName, facilityId, reportingYear, exportType, filter, columnVisibility]
  );

  const handleClose = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleDownloadClick = useCallback(() => {
    alerts.success(`Downloading file...`);
    downloadFile();
    handleClose();
  }, [downloadFile, alerts, handleClose]);

  const handleExportClick = useCallback(async () => {
    const params = getExportParams();

    if (dialog) {
      setIsDialogOpen(true);
    }

    alerts.success(`Generating file...`);

    // Start export - auto-download is handled by the hook based on the autoDownload option
    await startExport(params);
  }, [getExportParams, startExport, alerts, dialog]);

  const isLoading = status === "loading";

  return (
    <>
      {!iconOnly ? (
        <Button
          variant="text"
          size="medium"
          startIcon={<FileDownload />}
          loading={isLoading}
          onClick={handleExportClick}
          disabled={props.disabled}
        >
          {title}
        </Button>
      ) : (
        <Tooltip title={title}>
          <IconButton
            size="medium"
            color="info"
            loading={isLoading}
            onClick={handleExportClick}
            disabled={props.disabled}
          >
            <FileDownload />
          </IconButton>
        </Tooltip>
      )}

      {dialog && (
        <Dialog
          title={dialogTitle}
          open={isDialogOpen}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          primaryActionButtonProps={{
            loading: isLoading,
            disabled: isLoading,
            onClick: handleDownloadClick,
            startIcon: <Download />,
            saveText: "Download",
          }}
        >
          {status === "loading" ? (
            <Typography>{generateMessage}</Typography>
          ) : (
            <>
              <Typography>
                Your file has been generated and should download automatically.
              </Typography>
              <Typography>
                If the download didn't start, you can{" "}
                <Link
                  style={{ cursor: "pointer" }}
                  onClick={handleDownloadClick}
                  className="text-blue-600 underline"
                >
                  click here
                </Link>{" "}
                to download it manually.
              </Typography>
            </>
          )}
        </Dialog>
      )}
    </>
  );
};
